import React, { useCallback, useEffect, useState } from 'react';
import { usePaymentTerms } from '@/clients/finance/usePaymentTerms.ts';
import { generateSummaryFinanceSettingsDefaultValues } from '@/shared/components/FinanceSettings/utils/generateSummaryFinanceSettingsDefaultValues.ts';
import { BILLING_TYPE, PRODUCT_CATEGORY } from '@schuettflix/interfaces';
import { PaymentTerm } from '@/clients/finance/types/FinanceSettings.ts';
import { PaymentTermsInput } from '@/shared/inputs/PaymentTermsInput.tsx';
import { BILLING_TYPE_LABEL } from '@/shared/components/FinanceSettings/BillingTypeLabels.ts';
import { useTranslation } from 'react-i18next';
import { TextBoxFieldInput } from '@/shared/inputs/TextBoxFieldInput.tsx';
import { SwitchField } from '@schuettflix/planum-react';
import { UseControllerProps, useFormContext } from 'react-hook-form';
import { useFormClear } from '@/shared/hooks/useFormClear.ts';
import { FinanceSettings } from '@/shared/models/ContextDataModel/interfaces/entities/finance-settings.ts';
import { UpsertFinanceSettings } from '@/shared/models/ContextDataModel/ContextData.ts';
import { useOrganizationById } from '@/clients/organization/useOrganization.ts';
import { FinanceSettingsFormValues } from '../schema/getFinanceSettingsFormSchema.ts';

type FinanceSettingsIndexPath<TField extends string> = `financeSettings.${number}.${TField}`;

type FinanceSettingsPaymentTermsProps = {
    organizationId: number;
    isRevenueIncreasingCase: boolean;
    includedProductCategories: PRODUCT_CATEGORY[];
    disabled?: boolean;
    creditorOrganizationIdProps: Pick<
        UseControllerProps<FinanceSettingsFormValues, FinanceSettingsIndexPath<'creditorOrganizationId'>>,
        'name' | 'defaultValue'
    >;
    debtorOrganizationIdProps: Pick<
        UseControllerProps<FinanceSettingsFormValues, FinanceSettingsIndexPath<'debtorOrganizationId'>>,
        'name' | 'defaultValue'
    >;
    paymentTermsIdProps: Pick<
        UseControllerProps<FinanceSettingsFormValues, FinanceSettingsIndexPath<'paymentTermId'>>,
        'name' | 'defaultValue'
    >;
    billingMethodProps: Pick<
        UseControllerProps<FinanceSettingsFormValues, FinanceSettingsIndexPath<'billingMethod'>>,
        'name' | 'defaultValue'
    >;
    billingTextProps: Pick<
        UseControllerProps<FinanceSettingsFormValues, FinanceSettingsIndexPath<'billingText'>>,
        'name' | 'defaultValue'
    >;
    onChange?: (financeSettings: UpsertFinanceSettings) => void;
};

export const FinanceSettingsPaymentTerms = ({
    organizationId,
    isRevenueIncreasingCase,
    includedProductCategories,
    creditorOrganizationIdProps,
    debtorOrganizationIdProps,
    paymentTermsIdProps,
    billingMethodProps,
    billingTextProps,
    onChange,
}: FinanceSettingsPaymentTermsProps) => {
    const { t } = useTranslation();
    const { data: addressedOrganization } = useOrganizationById(organizationId);
    const { data: paymentTerms, isLoading: isLoadingPaymentTerms } = usePaymentTerms();

    const methods = useFormContext<FinanceSettingsFormValues>();
    const { clearField, resetField, getValues } = useFormClear(methods);

    const { defaultValues: fallBackPaymentTermsDefaultValues } = generateSummaryFinanceSettingsDefaultValues({
        paymentTermsSettings: paymentTerms,
        addressedOrganization,
        isRevenueIncreasingCase,
        includedProductCategories,
    });

    const availablePaymentTerms: PaymentTerm[] = paymentTerms.paymentTerms.filter(
        ({ eligibleForRevenueIncrease, eligibleForCostIncrease }) => {
            return isRevenueIncreasingCase ? eligibleForRevenueIncrease : eligibleForCostIncrease;
        }
    );

    const availableBillingMethods: BILLING_TYPE[] = isRevenueIncreasingCase
        ? ['INVOICE']
        : ['CREDIT_NOTE', 'PARTNER_INVOICE'];

    const defaultPaymentTermId =
        paymentTermsIdProps.defaultValue !== undefined
            ? paymentTermsIdProps.defaultValue
            : fallBackPaymentTermsDefaultValues?.paymentTermId;
    const defaultBillingMethod = billingMethodProps.defaultValue ?? fallBackPaymentTermsDefaultValues?.billingMethod;

    const [isBillingTextVisible, setIsBillingTextVisible] = useState(defaultBillingMethod !== 'PARTNER_INVOICE');
    const [isTextBoxOpen, setIsTextBoxOpen] = useState(!!billingTextProps.defaultValue);

    useEffect(() => {
        resetField(paymentTermsIdProps.name, {
            defaultValue: defaultPaymentTermId,
        });

        resetField(billingMethodProps.name, {
            defaultValue: defaultBillingMethod,
        });

        resetField(billingTextProps.name, {
            defaultValue: billingTextProps.defaultValue,
        });
    }, [
        defaultPaymentTermId,
        paymentTermsIdProps.name,
        defaultBillingMethod,
        billingMethodProps.name,
        billingTextProps.defaultValue,
        billingTextProps.name,
        resetField,
    ]);

    const handleBillingMethodChange = useCallback(
        (billingMethod: BILLING_TYPE | undefined) => {
            const billingText = getValues(billingTextProps.name);
            const paymentTermId = getValues(paymentTermsIdProps.name);
            const creditorOrganizationId = getValues(creditorOrganizationIdProps.name);
            const debtorOrganizationId = getValues(debtorOrganizationIdProps.name);

            if (billingMethod === 'PARTNER_INVOICE') {
                clearField(billingTextProps.name);
            }

            setIsBillingTextVisible(billingMethod !== 'PARTNER_INVOICE');

            onChange?.({
                creditorOrganizationId,
                debtorOrganizationId,
                paymentTermId,
                billingText,
                billingMethod,
            } as FinanceSettings);
        },
        [
            billingTextProps.name,
            paymentTermsIdProps.name,
            creditorOrganizationIdProps.name,
            debtorOrganizationIdProps.name,
            getValues,
            clearField,
            onChange,
        ]
    );

    const handlePaymentTermIdChange = useCallback(
        (paymentTermId: number | null) => {
            const billingText = getValues(billingTextProps.name);
            const billingMethod = getValues(billingMethodProps.name);
            const creditorOrganizationId = getValues(creditorOrganizationIdProps.name);
            const debtorOrganizationId = getValues(debtorOrganizationIdProps.name);

            onChange?.({
                creditorOrganizationId,
                debtorOrganizationId,
                paymentTermId,
                billingText,
                billingMethod,
            } as FinanceSettings);
        },
        [
            billingMethodProps.name,
            billingTextProps.name,
            creditorOrganizationIdProps.name,
            debtorOrganizationIdProps.name,
            getValues,
            onChange,
        ]
    );

    return (
        <>
            <PaymentTermsInput
                paymentTermsIdProps={{
                    defaultValue: defaultPaymentTermId,
                    name: paymentTermsIdProps.name,
                    disabled: isLoadingPaymentTerms,
                    options: availablePaymentTerms.map(({ id, description }) => ({
                        value: id,
                        label: description,
                    })),
                    onChangePaymentTermsId: handlePaymentTermIdChange,
                }}
                billingMethodProps={{
                    defaultValue: defaultBillingMethod,
                    name: billingMethodProps.name,
                    disabled: isLoadingPaymentTerms,
                    options: availableBillingMethods.map(billingMethod => ({
                        value: billingMethod,
                        label: t(BILLING_TYPE_LABEL[billingMethod]),
                    })),
                    onBillingMethodChange: handleBillingMethodChange,
                }}
            />
            {isBillingTextVisible && (
                <div className="flex flex-col gap-6 pt-2">
                    <SwitchField
                        label={t('order.summary.toggleSwitch.label')}
                        size="sm"
                        className="w-fit"
                        checked={isTextBoxOpen}
                        onCheckedChange={() => {
                            setIsTextBoxOpen(prev => {
                                if (prev) {
                                    clearField(billingTextProps.name);

                                    const creditorOrganizationId = getValues(creditorOrganizationIdProps.name);
                                    const debtorOrganizationId = getValues(debtorOrganizationIdProps.name);

                                    onChange?.({
                                        creditorOrganizationId,
                                        debtorOrganizationId,
                                        billingText: null,
                                    });
                                }
                                return !prev;
                            });
                        }}
                    />
                    {isTextBoxOpen && (
                        <TextBoxFieldInput
                            name={billingTextProps.name}
                            defaultValue={billingTextProps.defaultValue}
                            placeHolder={t('order.summary.textAreaField.placeholder')}
                            label={t('order.summary.textAreaField.label')}
                            description={t('order.summary.textAreaField.description')}
                            rows={4}
                            onTextBoxChange={billingText => {
                                const creditorOrganizationId = getValues(creditorOrganizationIdProps.name);
                                const debtorOrganizationId = getValues(debtorOrganizationIdProps.name);

                                onChange?.({
                                    creditorOrganizationId,
                                    debtorOrganizationId,
                                    billingText,
                                });
                            }}
                        />
                    )}
                </div>
            )}
        </>
    );
};
